import React from 'react';
import { Card, Typography, Empty, Radio } from 'antd';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid
} from 'recharts';

const { Title } = Typography;

interface ClicksOverTimeGraphProps {
    clicksOverTime: {
        granularity: 'day' | 'month';
        data: { label: string; value: number }[];
    };
    onGranularityChange?: (g: 'day' | 'month') => void;
}

const ClicksOverTimeGraph: React.FC<ClicksOverTimeGraphProps> = ({ clicksOverTime, onGranularityChange }) => {
    const { granularity, data } = clicksOverTime;

    const formatLabel = (label: string, granularity: 'day' | 'month') => {
        if (granularity === 'month') {
            const [year, month] = label.split('-').map(Number);
            return new Date(year, month - 1).toLocaleString('en-US', {
                month: 'long'
            });
        }

        const date = new Date(label);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short'
        });
    };

    return (
        <Card style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ marginBottom: 0 }}>
                    Clicks over time
                </Title>

                {onGranularityChange && (
                    <Radio.Group
                        size="small"
                        value={granularity}
                        onChange={(e) => {
                            e.preventDefault();
                            onGranularityChange?.(e.target.value);
                        }}
                    >
                        <Radio.Button value="day">Day</Radio.Button>
                        <Radio.Button value="month">Month</Radio.Button>
                    </Radio.Group>
                )}
            </div>

            <div style={{ marginTop: 16 }}>
                {(!data || data.length === 0) ? (
                    <Empty description="No clicks data available" />
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="label"
                                tickFormatter={(label) => formatLabel(label, granularity)}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#1677ff"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </Card>
    );
};

export default ClicksOverTimeGraph;